import type { Faction, Option, OptionCategory, OptionChoice, OptionScope } from '../../types/faction';

// Raw JSON imports
import bretonniaRaw from './kingdom-of-bretonnia.json';
import empireRaw from './empire-of-man.json';
import dwarvesRaw from './dwarfen-mountain-holds.json';
import woodElvesRaw from './wood-elf-realms.json';
import highElvesRaw from './high-elf-realms.json';
import orcsRaw from './orc-and-goblin-tribes.json';
import warriorsRaw from './warriors-of-chaos.json';
import beastmenRaw from './beastmen-brayherds.json';
import tombKingsRaw from './tomb-kings-of-khemri.json';
import vampireCountsRaw from './vampire-counts.json';
import darkElvesRaw from './dark-elves.json';
import grandCathayRaw from './grand-cathay.json';
import realmsOfMenRaw from './realms-of-men.json';
import hordesOfChaosRaw from './hordes-of-chaos.json';
import chaosDwarfsRaw from './chaos-dwarfs.json';
import vampireCountsRenegadesRaw from './vampire-counts-renegades.json';
import darkElvesRenegadesRaw from './dark-elves-renegades.json';
import chaosDwarfsRenegadesRaw from './chaos-dwarfs-renegades.json';
import lizardmenRenegadesRaw from './lizardmen-renegades.json';
import ogreKingdomsRenegadesRaw from './ogre-kingdoms-renegades.json';
import skavenRenegadesRaw from './skaven-renegades.json';
import daemonsRenegadesRaw from './daemons-of-chaos-renegades.json';
import lizardmenRaw from './lizardmen.json';
import daemonsRaw from './daemons-of-chaos.json';
import ogresRaw from './ogre-kingdoms.json';
import skavenRaw from './skaven.json';
import chaosDwarfsRlRaw from './chaos-dwarfs-renegade-legacy.json';
import daemonsRlRaw from './daemons-of-chaos-renegade-legacy.json';
import darkElvesRlRaw from './dark-elves-renegade-legacy.json';
import vampireCountsRlRaw from './vampire-counts-renegade-legacy.json';
import lizardmenRlRaw from './lizardmen-renegade-legacy.json';
import ogresRlRaw from './ogre-kingdoms-renegade-legacy.json';
import skavenRlRaw from './skaven-renegade-legacy.json';
import globalMagicItemsRaw from '../magic-items.json';

/**
 * Normalise the options array on a unit so all factions use the same
 * { description, cost, scope, condition?, replaces?, max_points? } shape.
 *
 * Some faction files (e.g. Empire) use { name, cost, scope } for individual
 * options and { description, choices: [...] } for mutually-exclusive groups.
 * We flatten choices into individual options, carrying the group header as
 * the `condition` field so the UI can still surface it.
 */
function normalizeOptions(options: unknown[] | undefined): Option[] {
  if (!options) return [];
  const result: Option[] = [];

  for (const raw of options) {
    const opt = raw as Record<string, unknown>;

    if (Array.isArray(opt.choices)) {
      // Mutually-exclusive group: preserve as a choice group with choices[]
      const choices: OptionChoice[] = (opt.choices as Record<string, unknown>[]).map((c) => ({
        description: (c.description ?? c.name ?? '') as string,
        points: (c.points ?? 0) as number,
        scope: (c.scope ?? 'per_unit') as OptionScope,
        notes: c.notes as string | undefined,
        category: c.category as OptionCategory | undefined,
        grants_rules: c.grants_rules as string[] | undefined,
        removes_rules: c.removes_rules as string[] | undefined,
        attaches_unit_id: c.attaches_unit_id as string | undefined,
        max_per_1000_pts: c.max_per_1000_pts as number | undefined,
        max_per_army: c.max_per_army as number | undefined,
        cap_pool: c.cap_pool as string | undefined,
      }));
      result.push({
        description: (opt.description ?? opt.name ?? '') as string,
        points: 0,
        scope: 'per_unit',
        condition: opt.condition as string | undefined,
        condition_not: opt.condition_not as string | undefined,
        notes: opt.notes as string | undefined,
        category: opt.category as OptionCategory | undefined,
        multi_select: opt.multi_select as boolean | undefined,
        required: opt.required as boolean | undefined,
        max_per_1000_pts: opt.max_per_1000_pts as number | undefined,
        max_per_army: opt.max_per_army as number | undefined,
        cap_pool: opt.cap_pool as string | undefined,
        requires_general_unit_ids: opt.requires_general_unit_ids as string[] | undefined,
        requires_wizard: opt.requires_wizard as boolean | undefined,
        choices,
      });
    } else {
      result.push({
        description: (opt.description ?? opt.name ?? '') as string,
        points: (opt.points ?? 0) as number,
        scope: (opt.scope ?? 'per_unit') as OptionScope,
        condition: opt.condition as string | undefined,
        condition_not: opt.condition_not as string | undefined,
        notes: opt.notes as string | undefined,
        replaces: opt.replaces as string | undefined,
        max_points: opt.max_points as number | undefined,
        max_count: opt.max_count as number | undefined,
        per_n_models: opt.per_n_models as number | undefined,
        category: opt.category as OptionCategory | undefined,
        grants_rules: opt.grants_rules as string[] | undefined,
        removes_rules: opt.removes_rules as string[] | undefined,
        attaches_unit_id: opt.attaches_unit_id as string | undefined,
        max_per_1000_pts: opt.max_per_1000_pts as number | undefined,
        max_per_army: opt.max_per_army as number | undefined,
        cap_pool: opt.cap_pool as string | undefined,
        requires_general_unit_ids: opt.requires_general_unit_ids as string[] | undefined,
        requires_wizard: opt.requires_wizard as boolean | undefined,
      });
    }
  }

  return result;
}

// Build lookup map: id → canonical data from global magic items
type GlobalItemData = {
  description?: string;
  rules_text?: string;
  restrictions?: string;
  single_use?: boolean;
  is_shield?: boolean;
  grants_rules?: string[];
  weapon_profile?: Record<string, unknown>;
  armour_profile?: Record<string, unknown>;
};
const GLOBAL_ITEMS = (globalMagicItemsRaw as { magic_items: Record<string, unknown>[] }).magic_items;

/**
 * Factions whose models cannot buy Common magic items, and so draw nothing from
 * the shared pool — including the Battle March items, which are purchased in
 * exactly the same way. Dwarfs forge runic items instead; Daemons of Chaos take
 * Daemonic Gifts and Icons, and their book states outright that "models within a
 * Daemons of Chaos army cannot purchase Common magic items".
 */
const NO_COMMON_ITEMS_FACTION_IDS: ReadonlySet<string> = new Set([
  'dwarfen-mountain-holds',
  'daemons-of-chaos',
  'daemons-of-chaos-renegades',
  'daemons-of-chaos-renegade-legacy',
]);

/**
 * Lives in the global pool but belongs to the Dwarfs' Dragon Company rather than
 * being a common item, so it is never handed out with the commons.
 */
const DWARF_ONLY_GLOBAL_IDS: ReadonlySet<string> = new Set(['dragonblade']);

// ---------------------------------------------------------------------------
// Stat-shape normalisation: the app renders `profiles` only, but three legacy
// storage shapes survive in the data (see BACKLOG "Three stat-storage
// conventions"). Convert them here so every unit gets a stat line.
// ---------------------------------------------------------------------------

const STAT_KEYS = ['M', 'WS', 'BS', 'S', 'T', 'W', 'I', 'A', 'Ld'] as const;

/** A full stat row for display - '-' where the book prints a dash (crew rows omit M/T/W). */
function fullProfile(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of STAT_KEYS) out[k] = raw[k] ?? '-';
  return out;
}

/** "crew_bestigor" → "Bestigor Crew", "tuskgor_x2" → "Tuskgor (x2)", "gor" → "Gor" */
function subProfileLabel(key: string): string {
  let name = key;
  let suffix = '';
  const xn = /_x(\d+)$/.exec(name);
  if (xn) { suffix = ` (x${xn[1]})`; name = name.slice(0, xn.index); }
  let crew = false;
  if (name.startsWith('crew_')) { crew = true; name = name.slice(5); }
  const title = name.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
  return title + (crew ? ' Crew' : '') + suffix;
}

interface RawCommand { role?: string; name?: string; cost_per_unit?: number }

/** Build a profiles array from stats_by_model / nested stats when the unit has none. */
function deriveProfiles(u: Record<string, unknown>): unknown[] | undefined {
  const category = u.category as string;
  const champ = (u.command as RawCommand[] | undefined)?.find((c) => c.role === 'champion');

  let profiles: { name: string; profile: Record<string, unknown>; is_mount?: boolean; is_champion?: boolean; champion_cost?: number }[] | undefined;

  if (Array.isArray(u.stats_by_model)) {
    // Rows like { model: "Boar Boy" | "Boss" | "War Boar", M, WS, … }. The
    // champion row matches the command champion's name; on cavalry the last
    // non-crew row is the mount (chariot beasts, riding beasts).
    const rows = u.stats_by_model as Record<string, unknown>[];
    profiles = rows.map((r, i) => {
      const name = String(r.model ?? u.name);
      const entry: NonNullable<typeof profiles>[number] = { name, profile: fullProfile(r) };
      if (champ?.name && name.toLowerCase() === champ.name.toLowerCase()) {
        entry.is_champion = true;
        if (champ.cost_per_unit !== undefined) entry.champion_cost = champ.cost_per_unit;
      } else if (category === 'cavalry' && i === rows.length - 1 && i > 0 && !/crew/i.test(name)) {
        entry.is_mount = true;
      }
      return entry;
    });
  } else if (u.stats && typeof u.stats === 'object') {
    const stats = u.stats as Record<string, unknown>;
    const subs = Object.entries(stats).filter(([, v]) => typeof v === 'object' && v !== null);
    if (subs.length === 0) {
      // Flat stats object - the long-standing conversion, unchanged.
      return [{ name: u.name as string, profile: stats }];
    }
    // Flat stats with nested sub-rows (Tuskgor Chariot crew_*/tuskgor_x2,
    // Primal Warherd gor/ungor).
    profiles = [];
    if (STAT_KEYS.some((k) => stats[k] !== undefined)) {
      profiles.push({ name: u.name as string, profile: fullProfile(stats) });
    }
    for (const [key, sub] of subs) {
      const entry: (typeof profiles)[number] = { name: subProfileLabel(key), profile: fullProfile(sub as Record<string, unknown>) };
      if (category === 'cavalry' && /_x\d+$/.test(key) && !key.startsWith('crew')) entry.is_mount = true;
      profiles.push(entry);
    }
  }

  if (!profiles?.length) return undefined;
  // Convention: the mount's M belongs in the body row, not '-' (chariots).
  const main = profiles[0];
  const mountRow = profiles.find((p) => p.is_mount);
  if (mountRow && String(main.profile.M) === '-' && String(mountRow.profile.M) !== '-') {
    main.profile = { ...main.profile, M: mountRow.profile.M };
  }
  return profiles;
}

const globalItemData = new Map<string, GlobalItemData>();
for (const item of GLOBAL_ITEMS) {
  globalItemData.set(item.id as string, {
    description: item.description as string | undefined,
    rules_text: item.rules_text as string | undefined,
    restrictions: item.restrictions as string | undefined,
    single_use: item.single_use as boolean | undefined,
    is_shield: item.is_shield as boolean | undefined,
    grants_rules: item.grants_rules as string[] | undefined,
    weapon_profile: item.weapon_profile as Record<string, unknown> | undefined,
    armour_profile: item.armour_profile as Record<string, unknown> | undefined,
  });
}

function normalizeFaction(raw: unknown): Faction {
  const f = raw as Record<string, unknown>;
  // Normalise faction magic items: canonical data wins over faction copies for global items
  const factionMagicItems: Record<string, unknown>[] = (f.magic_items as Record<string, unknown>[] | undefined ?? []).map((item) => {
    const globals = globalItemData.get(item.id as string);
    return {
      ...item,
      // For global items: canonical data wins over faction copies
      description: globals?.description ?? (item.description ?? item.effect),
      rules_text: globals?.rules_text ?? (item.rules_text as string | undefined),
      restrictions: globals !== undefined ? globals.restrictions : item.restrictions,
      single_use: globals?.single_use ?? (item.single_use as boolean | undefined),
      is_shield: globals?.is_shield ?? (item.is_shield as boolean | undefined),
      grants_rules: globals?.grants_rules ?? (item.grants_rules as string[] | undefined),
      weapon_profile: globals?.weapon_profile ?? item.weapon_profile,
      armour_profile: globals?.armour_profile ?? item.armour_profile,
      // An item that exists in the global pool IS a Common. Borrowed models in a
      // two-parent list buy from their parent book plus the Commons, and this is
      // what tells the two apart - a faction-native item has no origin either.
      is_common: globals !== undefined ? true : (item.is_common as boolean | undefined),
    };
  });

  // Append any common (or Battle March) item the faction's own list does not
  // already carry. Most faction files embed the commons already; the Legends and
  // Renegades lists do not, and picking them up here keeps the two in step
  // without duplicating 60-odd items into every JSON.
  if (!NO_COMMON_ITEMS_FACTION_IDS.has(f.id as string)) {
    const owned = new Set(factionMagicItems.map((i) => i.id as string));
    for (const item of GLOBAL_ITEMS) {
      const id = item.id as string;
      if (owned.has(id) || DWARF_ONLY_GLOBAL_IDS.has(id)) continue;
      // is_common marks runtime-merged rulebook items, so origin filtering can
      // tell them apart from a faction's own untagged items.
      factionMagicItems.push({ ...item, is_common: true });
    }
  }

  return {
    ...f,
    magic_items: factionMagicItems,
    units: (f.units as Record<string, unknown>[]).map((u) => {
      // Transform legacy stat shapes (flat stats, stats_by_model, nested
      // sub-rows) into the profiles format the app renders
      let profiles = u.profiles as unknown[];
      if (!profiles || profiles.length === 0) {
        profiles = deriveProfiles(u) ?? [];
      }
      // Command champion overrides live under either `champion_stats` or the
      // older `stats` key; the app reads champion_stats.
      const command = (u.command as Record<string, unknown>[] | undefined)?.map((c) =>
        c.stats && !c.champion_stats ? { ...c, champion_stats: c.stats } : c,
      );
      // Normalize weapon_profiles field names (strength → S, ap → AP)
      const weaponProfiles = (u.weapon_profiles as Record<string, unknown>[])?.map((wp) => ({
        ...wp,
        S: (wp.S ?? wp.strength) as string,
        AP: (wp.AP ?? wp.ap) as string,
      }));
      // Units that store per-model-type kit (e.g. O&G riders) leave `equipment` empty;
      // surface it as labelled strings so armour saves and displays can see it
      let equipment = (u.equipment as string[] | Record<string, string[]> | undefined) ?? [];
      if (Array.isArray(equipment) && equipment.length === 0 && Array.isArray(u.equipment_by_model)) {
        equipment = (u.equipment_by_model as { model: string; equipment: string[] }[]).map(
          (g) => `${g.model}: ${g.equipment.join(', ')}`
        );
      }
      return {
        ...u,
        profiles: profiles || [],
        equipment,
        special_rules: (u.special_rules as string[] | undefined) ?? [],
        options: normalizeOptions(u.options as unknown[] | undefined),
        ...(weaponProfiles && { weapon_profiles: weaponProfiles }),
        ...(command && { command }),
      };
    }),
  } as unknown as Faction;
}

export const FACTIONS: Faction[] = [
  // Forces of Fantasy
  normalizeFaction(bretonniaRaw),
  normalizeFaction(empireRaw),
  normalizeFaction(dwarvesRaw),
  normalizeFaction(woodElvesRaw),
  normalizeFaction(highElvesRaw),
  // Ravening Hordes
  normalizeFaction(orcsRaw),
  normalizeFaction(warriorsRaw),
  normalizeFaction(beastmenRaw),
  normalizeFaction(tombKingsRaw),
  // Legends
  normalizeFaction(vampireCountsRaw),
  normalizeFaction(darkElvesRaw),
  normalizeFaction(chaosDwarfsRaw),
  normalizeFaction(lizardmenRaw),
  normalizeFaction(daemonsRaw),
  normalizeFaction(ogresRaw),
  normalizeFaction(skavenRaw),
  // Renegade Legacy Pack v1.5 (community)
  normalizeFaction(chaosDwarfsRlRaw),
  normalizeFaction(daemonsRlRaw),
  normalizeFaction(darkElvesRlRaw),
  normalizeFaction(vampireCountsRlRaw),
  normalizeFaction(lizardmenRlRaw),
  normalizeFaction(ogresRlRaw),
  normalizeFaction(skavenRlRaw),
  // Arcane Journals (FoF)
  normalizeFaction(grandCathayRaw),
  // Arcane Journals (War of Settra's Fury)
  normalizeFaction(realmsOfMenRaw),
  normalizeFaction(hordesOfChaosRaw),
  // Square Based / Renegades Legends (community)
  normalizeFaction(vampireCountsRenegadesRaw),
  normalizeFaction(darkElvesRenegadesRaw),
  normalizeFaction(chaosDwarfsRenegadesRaw),
  normalizeFaction(lizardmenRenegadesRaw),
  normalizeFaction(ogreKingdomsRenegadesRaw),
  normalizeFaction(skavenRenegadesRaw),
  normalizeFaction(daemonsRenegadesRaw),
];

export function getFaction(id: string): Faction | undefined {
  return FACTIONS.find((f) => f.id === id);
}
